import Phaser from 'phaser';
import { t, toggleLang } from '../../i18n';
import { getMetaProgression, type MetaUpgradeId } from '../systems/MetaProgression';
import { hapticImpactLight, setTelegramBackButton } from '../../telegram/webApp';

interface UpgradeRow {
  id: MetaUpgradeId;
  bonus: string;
}

const UPGRADE_ROWS: UpgradeRow[] = [
  { id: 'maxHp', bonus: '+10%' },
  { id: 'startingDamage', bonus: '+5%' },
  { id: 'moveSpeed', bonus: '+3%' },
  { id: 'startingLevel', bonus: '+1' },
];

export class ShopScene extends Phaser.Scene {
  private readonly meta = getMetaProgression();
  private coinsText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super('Shop');
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor('#0f172a');
    this.cameras.main.fadeIn(220, 0, 0, 0);

    // Telegram: back button returns to Menu
    setTelegramBackButton(() => this.transitionTo('Menu'));

    this.add
      .text(width / 2, 24, `🛒 ${t('shop.title')}`, {
        fontSize: '30px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0);

    this.coinsText = this.add
      .text(width / 2, 70, '', {
        fontSize: '20px',
        color: '#ffdd44',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0);

    let y = 120;
    for (const row of UPGRADE_ROWS) {
      this.buildUpgradeRow(width, y, row);
      y += 96;
    }

    const backBtn = this.add
      .text(width / 2, height - 44, `← ${t('shop.back')}`, {
        fontSize: '24px',
        color: '#44aaff',
        backgroundColor: '#16213e',
        padding: { x: 20, y: 10 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    backBtn.on('pointerdown', () => {
      hapticImpactLight();
      this.transitionTo('Menu');
    });
    backBtn.on('pointerover', () => backBtn.setStyle({ color: '#ffffff' }));
    backBtn.on('pointerout', () => backBtn.setStyle({ color: '#44aaff' }));

    const langBtn = this.add
      .text(width - 12, 16, t('menu.lang'), {
        fontSize: '18px',
        color: '#ffffff',
        backgroundColor: '#16213e',
        padding: { x: 10, y: 6 },
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });

    langBtn.on('pointerdown', () => {
      toggleLang();
      this.scene.restart();
    });

    this.refreshCoins();
  }

  private buildUpgradeRow(width: number, y: number, row: UpgradeRow): void {
    const rowBg = this.add.graphics();
    rowBg.fillStyle(0x16213e, 1);
    rowBg.fillRoundedRect(12, y - 8, width - 24, 80, 10);
    rowBg.lineStyle(1, 0x284b7a, 1);
    rowBg.strokeRoundedRect(12, y - 8, width - 24, 80, 10);

    const levelText = this.add
      .text(24, y + 28, '', {
        fontSize: '15px',
        color: '#aaccee',
      })
      .setOrigin(0, 0);

    this.add
      .text(24, y, `${t(`shop.upgrade.${row.id}`)} (${row.bonus})`, {
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);

    const buyBtn = this.add
      .text(width - 20, y + 32, '', {
        fontSize: '14px',
        color: '#00ff88',
        backgroundColor: '#0d2a1a',
        padding: { x: 10, y: 6 },
      })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });

    const render = () => {
      const level = this.meta.getUpgradeLevel(row.id);
      const cost = this.meta.getUpgradeCost(row.id);
      levelText.setText(`${t('shop.level')}: ${level}/5`);

      if (cost === null) {
        buyBtn.setText(t('shop.max'));
        buyBtn.disableInteractive();
        buyBtn.setStyle({ color: '#999999', backgroundColor: '#2a2a2a' });
      } else if (this.meta.getCoins() < cost) {
        buyBtn.setText(`${t('shop.buy')} (${cost})`);
        buyBtn.disableInteractive();
        buyBtn.setStyle({ color: '#999999', backgroundColor: '#2a2a2a' });
      } else {
        buyBtn.setText(`${t('shop.buy')} (${cost})`);
        buyBtn.setStyle({ color: '#00ff88', backgroundColor: '#0d2a1a' });
        if (!buyBtn.input?.enabled) buyBtn.setInteractive({ useHandCursor: true });
      }
    };

    buyBtn.on('pointerdown', () => {
      if (this.meta.buyUpgrade(row.id)) {
        hapticImpactLight();
        this.refreshCoins();
        render();
      }
    });
    buyBtn.on('pointerover', () => {
      if (buyBtn.input?.enabled) buyBtn.setStyle({ color: '#ffffff' });
    });
    buyBtn.on('pointerout', () => {
      const cost = this.meta.getUpgradeCost(row.id);
      if (cost !== null && this.meta.getCoins() >= cost) {
        buyBtn.setStyle({ color: '#00ff88', backgroundColor: '#0d2a1a' });
      }
    });

    render();
  }

  private refreshCoins(): void {
    this.coinsText?.setText(`${t('shop.coins')}: ${this.meta.getCoins()}`);
  }

  private transitionTo(sceneKey: string): void {
    this.cameras.main.fadeOut(220, 0, 0, 0);
    this.time.delayedCall(220, () => this.scene.start(sceneKey));
  }
}
